<template>
	<div class="sidebar">
		<!--サイドバーの内容をここに追加-->
		<!--サイドバー閉じるボタン-->
		<button class="sidebar-close-btn" @click="$emit('close-sidebar')">✕</button>
		<!--サイドバーのタイトル-->
		<h2 class="sidebar-title">営農支援AI(仮称)</h2>
		<p>前回ログイン：{{ last_login_time }}</p>
		<div class="button-container">
			<!--「New Chat」ボタン-->
			<button class="btn newchat-button" @click="createChatButton">New Chat</button>
			<!-- データ更新ボタンの追加 -->
			<button class="btn data-update-button" @click="openDataUpdate">データ更新</button>
		</div>

		<!--余白確保エリア-->
		<div class="margin-area"></div>

		<!--サイドバーのボタン類-->
		<div class="sidebar-button">
			<div v-if="isSideBarLoading" class="sidebar-loading">Loading...</div>
			<div v-else>
				<!--サイドバー側のボタンと削除ボタンの高さを固定(このCSSの設定によってボタンが増えてスクロールが発生したとしてもボタンがずれることはない)-->
				<div class="conversations-container">
					<button
						v-for="(conversation, index) in conversations"
						:key="conversation.chat_session_id"
						:class="{ 'create-button': true, active: isActive(index) }"
						@click="selectButton(index, conversation.chat_session_id)"
						@mouseenter="showButton(index)"
						@mouseleave="hideButton(index)"
					>
						{{ checkCharacterNumber(conversation.oldest_question, index) }}

						<!--ゴミ箱ボタンを配置-->
						<div class="trashicon" v-show="showTrashButton[index]" @click="deleteItem(conversation.chat_session_id)">
							<img src="@/assets/TrashBox.png" alt="TrashBox.png" class="trash-icon" />
						</div>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { authFetch } from "@/utils/authFetch"; // 認証付きfetch（トークン付与などを内包）
import { useAuthStore } from "@/stores/authStore"; // Pinia ストア（userIdの取得に使用）
import { mapStores, mapState } from "pinia"; // Options APIでPiniaを使うためのヘルパ

export default {
	name: "SideBar",
	props: {
		// 初期表示フラグ：ページロード直後に最新セッションを表示するかどうか
		isInitialize: { type: Boolean, required: true },
		// 親側でのAPIリクエスト中フラグ：リクエスト中は各種ボタン操作を無効化
		loading: { type: Boolean, required: true },
		// 初期ローディング画面表示フラグ（UI用）
		isLoading: { type: Boolean, required: true },
		// 現在選択中の会話セッションID（親が保持している値を受け取る）
		conversation_id: { type: Number, required: false, default: 1 },
		// 現在のアクティブ行のバックアップ
		activeindex_backup: { type: Number, required: false, default: 0 },
		// 最終ログイン日時（UIに表示したい場合用の情報）
		last_login_time: { type: String, required: false, default: "" },
	},

	data() {
		return {
			// サイドバーに表示する「各セッションの最古プロンプト」の配列
			conversations: [],
			// 全ての会話履歴（本文表示に使う／抽出処理のソース）
			conversations_all: [],
			// サイドバーのどの行がアクティブ（選択状態）かを保持
			activeIndex: 0,
			// 新規会話作成時に採番したIDを保持（= nextSessionId()の結果）
			conversation_sessionid: 1,
			// ゴミ箱ボタンの表示状態（行インデックス→true/false）
			showTrashButton: {},
			// サイドバー内のロード状態（このコンポーネント内のみでON/OFF管理）
			isSideBarLoading: false,
			// Azure Functionsの接続先（環境変数から読み込み）
			function_ip: process.env.VUE_APP_API_URL,
			// userId の監視を解除するためのハンドラ退避
			unwatchUserId: null,
		};
	},

	computed: {
		// this.authStore でストアを参照可能にする
		...mapStores(useAuthStore),
		// ストアの userId をそのまま computed へ（Options APIでの取り回し簡略化）
		...mapState(useAuthStore, ["userId"]),
	},

	mounted() {
		// ページロード直後：既に userId が取れているなら即データ取得
		if (this.userId) {
			this.fetchConversations(); // CosmosDBから会話データ取得→初期表示へ
		}

		// userId が後から入る（認証完了後など）ケースに備えて監視を開始
		// 変化（空→非空）したタイミングでのみデータ取得を実行
		this.unwatchUserId = this.$watch(
			() => this.userId, // 監視対象：userId
			(newVal, oldVal) => {
				// 旧値が空で、新値が非空になった瞬間にだけ実行
				if (newVal && newVal !== oldVal) {
					this.fetchConversations();
				}
			},
			{ immediate: false } // mounted直後の即時実行はしない
		);
	},

	beforeUnmount() {
		// userId の監視を解除（メモリリーク回避）
		if (this.unwatchUserId) this.unwatchUserId();
	},

	methods: {
		/**
		 * 履歴配列（conversations_all の部分配列）を、チャット表示用の配列へ整形する。
		 * DRYのため複数箇所（初期表示・選択時）で再利用。
		 */
		toFormattedMessages(list) {
			if (!Array.isArray(list)) return [];
			return list.map((msg) => ({
				// フロントのチャットUI側で「user」「bot」の種別を使い分ける想定
				question: { text: msg.question, type: "user" },
				answer: { text: msg.answer, type: "bot" },

				// 画像URLをそのまま渡す
				imageUrl: msg.imageUrl,

				// 画像URLからファイル名だけ抽出（SASトークンなどは除去）
				imageFileNameList: this.getImageFileName(msg.imageUrl),

				// グラフ描画のための生データと種別（親側で判定・描画）
				graphData: msg.graphData,
				graphType: msg.graphType,
			}));
		},

		/**
		 * 次に割り振るセッションIDを決定する。
		 * 先頭+1ではなく、全履歴の「最大ID + 1」で採番し、重複・欠番への耐性を上げる。
		 */
		nextSessionId() {
			const ids = Array.isArray(this.conversations_all) ? this.conversations_all.map((m) => m.chat_session_id) : [];
			const maxId = ids.length ? Math.max(...ids) : 0;
			return maxId + 1;
		},

		/**
		 * 「New Chat」ボタン押下時の処理。
		 * - 親がリクエスト中なら操作不可
		 * - 新しいセッションIDを採番
		 * - サイドバー先頭に「New Conversation」を追加
		 * - 親へ「空履歴」＋「新セッションID」をemit
		 * - アクティブ行とゴミ箱表示を適切に更新
		 */
		createChatButton() {
			// 親のリクエスト中はUI操作禁止（誤操作防止）
			if (this.loading) return;

			// 新規セッションIDを決定し、内部にも保持
			const newId = this.nextSessionId();
			this.conversation_sessionid = newId;

			// サイドバーの表示配列が配列であることを保証し、先頭に追加
			if (!Array.isArray(this.conversations)) this.conversations = [];
			this.conversations.unshift({
				chat_session_id: newId,
				oldest_question: "New Conversation",
			});

			// 親へ「空の履歴」と「新セッションID」を通知（AIChat.vue側で初期UIを表示させる）
			this.$emit("session-selected", "", newId);

			// 以前のアクティブ行のゴミ箱を念のため非表示
			this.showTrashButton[this.activeIndex] = false;

			// 先頭行をアクティブに変更
			this.activeIndex = 0;

			// 新規行は履歴がないので、ゴミ箱は表示しない
			this.showTrashButton[0] = false;

			// 先頭アクティブ状態を親へバックアップ（サイドバー閉鎖時の復元用）
			this.$emit("index-backup", this.activeIndex);
		},

		/**
		 * 「データ更新」ボタン押下時の処理。
		 * - 同ウィンドウで /data-update に遷移（業務導線用）
		 * - 別タブで開くなら window.open(url, "_blank") に変更可能
		 */
		openDataUpdate() {
			const url = `${window.location.origin}/data-update`;
			window.location.href = url;
		},

		/**
		 * サイドバーの各行が「アクティブかどうか」を判定する。
		 * - テンプレート側でクラス付け（色付け・強調）に利用
		 */
		isActive(index) {
			return this.activeIndex === index;
		},

		/**
		 * サイドバーの会話行がクリックされたときの処理。
		 * - 親がリクエスト中なら操作不可
		 * - アクティブ行の更新＆バックアップ送信
		 * - 「New Conversation」かどうかでゴミ箱表示の有無を切り替え
		 * - 該当セッションIDの履歴を抽出・整形し、親へemit（チャット本文表示）
		 */
		selectButton(index, sessionId) {
			if (this.loading) return;

			// 直前のアクティブ行のゴミ箱は非表示（表示の暴発防止）
			this.showTrashButton[this.activeIndex] = false;
			// 新しいアクティブ行へ更新
			this.activeIndex = index;
			// アクティブ行インデックスを親へ通知（バックアップ用途）
			this.$emit("index-backup", this.activeIndex);

			// クリック行が「New Conversation」ならゴミ箱は非表示、履歴ありなら表示
			const isNew = this.conversations?.[index]?.oldest_question === "New Conversation";
			this.showTrashButton[index] = !isNew;

			// セッションIDが有効で、全履歴が存在する場合のみ履歴を抽出
			if (sessionId != null && Array.isArray(this.conversations_all)) {
				// 該当セッションの履歴だけを抽出
				const history = this.conversations_all.filter((msg) => msg.chat_session_id === sessionId);

				// チャットUI用に整形
				const formatted = this.toFormattedMessages(history);

				// 親へ抽出済み履歴とセッションIDを通知（本文に反映）
				this.$emit("session-selected", formatted, sessionId);
			} else {
				// 履歴が無い／IDが不明な場合は初期セッション「1」を通知（安全側）
				this.$emit("session-selected", "", 1);
			}
		},

		/**
		 * CosmosDBから会話データをまとめて取得し、初期表示のロジックを実行。
		 * ★isSideBarLoadingはこのメソッドの中だけでON/OFF管理（二重実行防止）。
		 * - 取得成功：初期表示フラグに応じて「最新セッション」or「現在選択セッション」を表示
		 * - 取得失敗：alert + 親へ loading-finished を通知
		 */
		async fetchConversations(silent = false) {
			// 実行中なら二重起動しない（API連打防止）
			if (this.isSideBarLoading) return;
			this.isSideBarLoading = true;

			try {
				// 接続先URLの組み立て
				const api_url = this.function_ip + "&action=GetAllConversations";

				// 認証付きfetchでPOST（認証トークンなどはauthFetch内で付与）
				const response = await authFetch(api_url, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
				});

				// JSONへパース
				const data = await response.json();

				// 取得配列の安全化（null/undefinedなら空配列）
				this.conversations = Array.isArray(data?.oldest_question) ? data.oldest_question : [];
				this.conversations_all = Array.isArray(data?.conversation_history) ? data.conversation_history : [];

				// サイドバーが 1 件だけの場合、その内容が「New Conversation」か確認
				const onlyOne = this.conversations.length === 1;
				const checkPrompt = onlyOne ? this.conversations[0].oldest_question : "";

				// silent 更新時は親の本文を再描画しない
				if (!silent) {
					// データがある場合（「New Conversation」単体ではない場合）
					if (checkPrompt !== "New Conversation") {
						// 初回表示フラグが立っているなら「最新セッション」を表示
						if (this.isInitialize) {
							// 全履歴から最大の chat_session_id を算出（=最新セッション）
							const ids = this.conversations_all.map((m) => m.chat_session_id);
							const latestSessionId = ids.length ? Math.max(...ids) : 1;

							// 最新セッションの履歴を本文に出す
							this.messageReadInit(latestSessionId);

							// 先頭行のゴミ箱表示（履歴あり前提のためtrue）
							this.showTrashButton[this.activeIndex] = false; // 念のため以前の行は消す
							this.showTrashButton[0] = true;

							// 先頭行をアクティブ表示へ
							this.activeIndex = 0;

							// 初回表示フラグを親側でfalseへ
							this.$emit("initialize-false");
						} else {
							// 初回表示ではない場合：親から渡された現在選択セッションを再表示
							const id = this.conversation_id ?? 1;
							this.messageReadInit(id);
						}
					} else {
						// 「New Conversation」しか無い（＝履歴ゼロ）のケース
						// 親へ空履歴＆初期セッションIDを通知し、UIを初期状態へ
						this.$emit("session-selected", "", 1);
						this.$emit("index-backup", 0);
						this.$emit("initialize-false");
					}
				}
			} catch (error) {
				// 取得失敗時：ユーザーへ通知
				alert("通信エラーが発生しました。接続先を確認してください。");

				// 親へローディング終了を通知（画面のスピナー等を止める）
				this.$emit("loading-finished");

				// デバッグログ
				console.error("Error fetching data:", error);
			} finally {
				// 成功／失敗に関わらず必ずロードフラグをOFF（UIを元に戻す）
				this.isSideBarLoading = false;
			}
		},

		/**
		 * サイドバーのテキスト表示（最古プロンプト）を行う際の整形。
		 * - 「New Conversation」はそのまま表示
		 * - それ以外は14文字を超える場合に末尾「...」で省略表示
		 * - ここではセーフティとして「New」はゴミ箱を非表示にしておく
		 */
		checkCharacterNumber(question, index) {
			const isNew = question === "New Conversation";

			// 「New」は常にゴミ箱非表示
			if (isNew) this.showTrashButton[index] = false;

			// 14文字超なら省略（UIの横幅に合わせて適宜調整可能）
			return isNew ? question : question.length > 14 ? question.substring(0, 14) + "..." : question;
		},

		/**
		 * 初期表示／再表示時に、指定セッションIDの履歴を抽出して親へ通知する。
		 * - 指定セッションに履歴が無ければ、サイドバー先頭のセッションへフォールバック
		 * - サイドバーが空の場合にも安全に動作するようガードを入れる
		 * - アクティブ行・ゴミ箱表示の整合もここで調整
		 */
		messageReadInit(latestSessionId) {
			// 指定セッションの履歴を抽出（無ければ空配列）
			const history = Array.isArray(this.conversations_all) ? this.conversations_all.filter((msg) => msg.chat_session_id === latestSessionId) : [];

			// 抽出結果が空：指定セッションに履歴が無いケース
			if (history.length === 0) {
				// サイドバー自体が空：履歴なしの完全初期状態
				if (!Array.isArray(this.conversations) || this.conversations.length === 0) {
					// 親へ空履歴＆初期セッションIDを通知（UIを初期状態に）
					this.$emit("session-selected", "", 1);

					// サイドバーのアクティブ行は 0（先頭）に設定
					this.activeIndex = 0;
					this.$emit("index-backup", 0);
					return;
				}

				// 先頭セッションIDへフォールバック（もっとも一般的な代替）
				const fallbackId = this.conversations[0].chat_session_id;

				// 先頭セッションの履歴を抽出
				const fallback = this.conversations_all.filter((msg) => msg.chat_session_id === fallbackId);

				// 表示用に整形
				const formatted = this.toFormattedMessages(fallback);

				// 先頭行のゴミ箱は履歴あり前提で表示
				this.showTrashButton[0] = true;

				// 先頭行をアクティブにして、親へバックアップ通知
				this.activeIndex = 0;
				this.$emit("index-backup", 0);

				// 親へ履歴とセッションIDを通知（チャット本文を差し替える）
				this.$emit("session-selected", formatted, fallbackId);
			} else {
				// 履歴が見つかった場合：そのまま表示
				const formatted = this.toFormattedMessages(history);

				// 現在選択中のセッションのゴミ箱を表示（復元されたアクティブ行に合わせる）
				const backupIdx = this.activeindex_backup ?? 0;
				this.showTrashButton[backupIdx] = true;
				// アクティブ行を親から受け取ったバックアップ値で復元
				this.activeIndex = backupIdx;

				// 親へ履歴とセッションIDを通知
				this.$emit("session-selected", formatted, latestSessionId);
			}
		},

		/**
		 * 画像URL配列からファイル名のみを抽出するユーティリティ。
		 * - SASトークン（?以降）を除去してから最後の「/」区切りの末尾を取得
		 * - 配列で無い／空配列の場合は null を返す
		 */
		getImageFileName(imageUrl) {
			if (Array.isArray(imageUrl) && imageUrl.length) {
				return imageUrl.map((url) => {
					const cleanUrl = String(url).split("?")[0]; // ?以降（SAS等）を除去
					return cleanUrl.split("/").pop(); // ファイル名だけ取得
				});
			}
			return null;
		},

		/**
		 * ゴミ箱ボタン表示（マウスオーバー時）。
		 * - 親がリクエスト中なら表示しない（UI一貫性のため）
		 * - 「New Conversation」行では表示しない（削除対象がないため）
		 */
		showButton(zoneId) {
			if (this.loading) return; // リクエスト中の操作防止
			const isNew = this.conversations?.[zoneId]?.oldest_question === "New Conversation";
			if (!isNew) this.showTrashButton[zoneId] = true;
		},

		/**
		 * ゴミ箱ボタン非表示（マウスアウト時）。
		 * - 「New Conversation」行や現在アクティブな行は非表示にしない
		 *   （アクティブ行は常時ゴミ箱を出しておくUIポリシーを想定）
		 */
		hideButton(zoneId) {
			const isNew = this.conversations?.[zoneId]?.oldest_question === "New Conversation";
			if (!isNew && this.activeIndex !== zoneId) {
				this.showTrashButton[zoneId] = false;
			}
		},

		/**
		 * 削除ダイアログの表示トリガ。
		 * - 親の confirm UI（@show-confirm）を起動
		 * - 削除対象セッションIDを親へ渡しておく（@session-backup）
		 */
		deleteItem(sessionId) {
			if (this.loading) return; // リクエスト中は操作不可
			this.$emit("show-confirm"); // 親側で確認ダイアログを表示
			this.$emit("session-backup", sessionId); // 親へ削除対象IDを退避
		},
	},
};
</script>

<style scoped>
/*サイドバーの見た目を調整*/
.sidebar {
	/*ダッシュボードと同じ幅・高さを採用*/
	width: 320px;
	height: 100vh;
	background-color: rgb(65, 103, 0);
	/*固定位置に指定*/
	position: fixed;
	top: 0;
	left: 0;
	/*サイドバーをできるだけ手前に描画(値が大きいほど、手前に描画される)*/
	z-index: 9999;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.title-area {
	text-align: center;
}
/*サイドバーのタイトル*/
.sidebar-title {
	color: white; /*文字の色を白に指定*/
	text-align: center; /*文字を中央寄せ*/
}

/*ボタンコンテナのスタイル*/
.button-container {
	display: flex; /* Flexboxを使用 */
	justify-content: space-around; /* ボタン間を均等に配置 */
	width: 100%; /* コンテナの幅を100%に設定 */
	max-width: 300px; /* 最大幅を設定（必要に応じて調整） */
	margin: 0 auto; /* 中央配置 */
}

/* ボタン共通スタイル */
.btn {
	margin-top: 1rem; /*上部に余白をつけて、位置を調整(1rem = 16px)*/
	padding: 0.5rem 1rem; /* 上下左右の余白 */
	border: none; /* ボタンの境界線なし */
	border-radius: 8px; /* ボタンの角の丸具合を調整 */
	color: white; /* フォントカラー */
	font-size: 16px; /* フォントサイズ */
	cursor: pointer; /* カーソルを合わせた時のマウスのマークを指定 */
	height: 40px;
}

/*「New Chat」ボタン*/
.newchat-button {
	background-color: rgb(54, 153, 203); /*ボタンの色を指定*/
}

/*「データ更新」ボタン*/
.data-update-button {
	background-color: rgb(29, 135, 189); /*ボタンの色を指定*/
}

/*サイドバーの閉じるボタン*/
.sidebar-close-btn {
	align-self: flex-end;
	background-color: transparent;
	border: none;
	cursor: pointer;
	font-size: 1.5rem;
	color: white;
	padding: 5px 10px 0 0;
}

/*余白確保エリア設定*/
.margin-area {
	height: 30px;
}

/*「New Chat」ボタン押下で新たに生成されたチャットボタン*/
.create-button {
	margin: 1px 0 1px 0.5rem; /*ボタンの配置を少し右寄りにする*/
	width: 95%; /*幅を指定*/
	padding: 0.7rem; /*上下左右の余白*/
	border: none; /*ボタンの境界線なし*/
	border-radius: 8px; /*ボタンの角の丸具合を調整*/
	color: white; /*フォントカラー*/
	font-size: 17px; /*フォントサイズ*/
	background-color: rgb(65, 103, 0); /*ボタンの色を指定*/
	cursor: pointer; /*カーソルを合わせた時のマウスのマークを指定*/
	text-align: left;
	display: flex;
}

/*生成されたボタンにカーソルを合わせた時には色を変化させる*/
.create-button:hover {
	background-color: rgb(142, 142, 142);
}

/*生成されたボタンが押下された時の色付け設定*/
.create-button.active {
	background-color: rgb(185, 185, 185);
}

/*生成されたボタンの数によって自動でスクロールバー表示*/
.sidebar-button {
	/* 縦方向のスクロールを許可 */
	overflow-y: auto;
	width: 100%;
	overflow-x: hidden;
}

/*ゴミ箱ボタン表示設定*/
.trashicon {
	position: absolute; /* 絶対位置を指定 */
	padding-top: 3px; /* 3px下に移動 */
	padding-right: 5px; /*右端からの幅を指定*/
	background-color: transparent; /*ボタンを透明に設定*/
	border: none;
	cursor: pointer;
}

/*ゴミ箱アイコン表示設定*/
.trash-icon {
	/*縦横の幅を指定*/
	height: 20px;
	width: 20px;
}

/*前回ログイン日時の表示設定*/
p {
	margin: 5px;
}

/* サイドバーのLoading表示 */
.sidebar-loading {
	color: white;
	text-align: center;
}

/* サイドバーのボタン群の表示設定 */
.conversations-container {
	/* 子要素の位置調整を可能に */
	/* この設定により、子要素であるゴミ箱ボタンが親要素についてくるようになるため、
  サイドバーのボタンが増えてスクロールされたとしても、そのまま位置が保持される */
	position: relative;
}

/*デザインA(スマートフォン)*/
/*スマートフォンサイズ、599px以下の時に適用するCSSを設定*/
@media screen and (max-width: 599px) {
	/*サイドバー側のタイトル*/
	.sidebar-title {
		/*フォントサイズをチャット画面のタイトルと同じサイズに縮小*/
		font-size: 16px;
	}

	/*前回ログイン時刻表示*/
	p {
		/*フォントサイズをチャット画面と同じサイズに縮小*/
		font-size: 15px;
	}

	/*「New Chat」ボタン*/
	.newchat-button {
		/*フォントサイズをチャット画面と同じサイズに縮小*/
		font-size: 15px;
	}

	/*サイドバーボタン*/
	.create-button {
		/*フォントサイズをチャット画面と同じサイズに縮小*/
		font-size: 15px;
	}
}
</style>
